import { axiosGet } from "@/services/axiosService";
import { useInfiniteQuery } from "@tanstack/react-query";
import React, {
  useMemo,
  useCallback,
  useState,
  useRef,
  useEffect,
} from "react";

// Preload fonts function
function preloadFonts(fonts) {
  fonts.forEach((font) => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = font.fontURL;
    document.head.appendChild(link);
  });
}

// Debounce hook
export const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Font fetching function with search support
export const fetchFontTypes = async (page = 1, search = "") => {
  try {
    const response = await axiosGet("/users/getFonts", {
      params: {
        page,
        limit: 10,
        search: search.trim(), // Trim whitespace
      },
    });

    if (!response.data) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const fontsData = response?.data?.data;
    const mappedFonts = fontsData
      .filter((font) => font.isActive)
      .map((font) => ({
        fontURL: font.url,
        fontFamily: font.name,
        displayName: font.name,
        _id: font._id,
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));

    // Preload fonts for better UX
    preloadFonts(mappedFonts);

    return {
      fonts: mappedFonts,
      pagination: response.data.pagination,
    };
  } catch (error) {
    console.error("Error fetching fonts:", error);

    // Return fallback fonts only on first page and no search
    if (page === 1 && !search.trim()) {
      const fallbackFonts = [
        {
          fontURL:
            "https://fonts.chatbotbuilder.net/1754155663486-h8o6CiQhT5Od-RobotoCondensed_Regular.css",
          fontFamily: "Roboto",
          displayName: "Roboto",
        },
      ];

      preloadFonts(fallbackFonts);

      return {
        fonts: fallbackFonts,
        pagination: {
          currentPage: 1,
          totalPages: 1,
          totalFonts: 2,
          limit: 10,
          hasNextPage: false,
          hasPrevPage: false,
          nextPage: null,
          prevPage: null,
        },
      };
    }
    throw error;
  }
};

// Infinite query hook with search support
export const useGetFonts = (search = "") => {
  // Debounce search to avoid too many API calls
  const debouncedSearch = useDebounce(search, 300);

  const infiniteQuery = useInfiniteQuery({
    queryKey: ["getFonts", debouncedSearch],
    queryFn: ({ pageParam = 1 }) => fetchFontTypes(pageParam, debouncedSearch),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.hasNextPage
        ? lastPage.pagination.nextPage
        : undefined;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes for search results
    gcTime: 1000 * 60 * 10, // 10 minutes cache
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    enabled: true,
  });

  // Flatten all pages into a single array
  const data = useMemo(() => {
    if (!infiniteQuery.data?.pages) return [];
    return infiniteQuery.data.pages.flatMap((page) => page.fonts);
  }, [infiniteQuery.data?.pages]);

  return {
    data,
    isLoading: infiniteQuery.isLoading,
    error: infiniteQuery.error,
    refetch: infiniteQuery.refetch,
    fetchNextPage: infiniteQuery.fetchNextPage,
    hasNextPage: infiniteQuery.hasNextPage,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    isError: infiniteQuery.isError,
  };
};

// Font loading CSS function
export const loadFontCSS = async (fontConfig) => {
  let fontURL, fontFamily, displayName;
  if (fontConfig?.displayName) {
    fontURL = fontConfig.fontURL;
    fontFamily = fontConfig.fontFamily;
    displayName = fontConfig.displayName;
  } else {
    fontURL = `https://fonts.chatbotbuilder.net/1779173781168-DSnS2AdoHjND-Roboto_Regular.css`;
    fontFamily = "roboto";
    displayName = "Roboto Regular";
  }
  try {
    console.log(`🔄 Loading font: ${displayName}`);

    const linkId = `font-link-${fontFamily.replace(/\s+/g, "-").toLowerCase()}`;
    let linkElement: any = document.getElementById(linkId);

    if (!linkElement) {
      linkElement = document.createElement("link");
      linkElement.id = linkId;
      linkElement.rel = "stylesheet";
      linkElement.href = fontURL;
      document.head.appendChild(linkElement);

      return new Promise((resolve, reject) => {
        linkElement.onload = () => {
          resolve(true);
        };
        linkElement.onerror = () => {
          console.error(`❌ Font failed: ${displayName}`);
          reject(false);
        };
      });
    }
    return true;
  } catch (error) {
    console.error(`❌ Font loading error for ${displayName}:`, error);
    return false;
  }
};

// Enhanced font loader hook
export const useFontLoader = () => {
  const [loadedFonts, setLoadedFonts] = useState(new Set());
  const [loadingFonts, setLoadingFonts] = useState(new Set());
  const [failedFonts, setFailedFonts] = useState(new Set());

  const loadFont = useCallback(
    async (fontConfig, retries = 1) => {
      if (loadedFonts.has(fontConfig.fontFamily)) {
        return true;
      }

      if (loadingFonts.has(fontConfig.fontFamily)) {
        return false;
      }

      if (failedFonts.has(fontConfig.fontFamily) && retries === 0) {
        return false;
      }

      setLoadingFonts((prev) => new Set([...prev, fontConfig.fontFamily]));

      try {
        const success = await loadFontCSS(fontConfig);

        setLoadingFonts((prev) => {
          const newSet = new Set(prev);
          newSet.delete(fontConfig.fontFamily);
          return newSet;
        });

        if (success) {
          setLoadedFonts((prev) => new Set([...prev, fontConfig.fontFamily]));
          setFailedFonts((prev) => {
            const newSet = new Set(prev);
            newSet.delete(fontConfig.fontFamily);
            return newSet;
          });
          return true;
        } else {
          if (retries > 0) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return await loadFont(fontConfig, retries - 1);
          } else {
            setFailedFonts((prev) => new Set([...prev, fontConfig.fontFamily]));
            return false;
          }
        }
      } catch (error) {
        console.error(
          `🚨 Font loading error for ${fontConfig.displayName}:`,
          error,
        );

        setLoadingFonts((prev) => {
          const newSet = new Set(prev);
          newSet.delete(fontConfig.fontFamily);
          return newSet;
        });

        if (retries > 0) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          return await loadFont(fontConfig, retries - 1);
        } else {
          setFailedFonts((prev) => new Set([...prev, fontConfig.fontFamily]));
          return false;
        }
      }
    },
    [loadedFonts, loadingFonts, failedFonts],
  );

  const preloadFonts = useCallback(
    async (fontConfigs: any, maxConcurrent: any = 3) => {
      const chunks: any = [];
      for (let i = 0; i < fontConfigs.length; i += maxConcurrent) {
        chunks.push(fontConfigs.slice(i, i + maxConcurrent));
      }

      for (const chunk of chunks) {
        await Promise.allSettled(
          chunk.map((fontConfig) => loadFont(fontConfig)),
        );
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    },
    [loadFont],
  );

  return { loadFont, preloadFonts, loadedFonts, loadingFonts, failedFonts };
};

// Main font types hook with search support
export const useFontTypes = (search = "") => {
  const [fontsPreloaded, setFontsPreloaded] = useState(false);
  const { preloadFonts } = useFontLoader();

  const {
    data: fontTypes = [],
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetFonts(search); // Pass search parameter here

  useEffect(() => {
    if (fontTypes.length > 0 && !fontsPreloaded) {
      const initialFonts = fontTypes.slice(0, Math.min(10, fontTypes.length));
      preloadFonts(initialFonts);
      setFontsPreloaded(true);
    }
  }, [fontTypes, fontsPreloaded, preloadFonts]);

  const preloadAllFonts = useCallback(async () => {
    if (fontTypes.length > 0) {
      const remainingFonts = fontTypes.slice(10);
      await preloadFonts(remainingFonts);
    }
  }, [fontTypes, preloadFonts]);

  const loadMoreFonts = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    fontTypes,
    isLoading,
    error: error?.message || null,
    refetch,
    preloadAllFonts,
    fontsPreloaded,
    loadMoreFonts,
    hasNextPage,
    isFetchingNextPage,
  };
};
